import { Modal, Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon/Icon';
import type { useTheme } from './theme';
import {
  REST_PICKER_MIN,
  REST_PICKER_MAX,
  REST_PICKER_STEP,
} from '@heraion/shared';
import { RestStepper } from './RestStepper';
import { makeStyles } from './loggerStyles';
import { stateAria } from "../a11y";

type RestDefaultPickerProps = {
  /** Mount gate — mirrors the original `restPickerOpen ? <Modal/> : null`
   *  conditional mount (not the Modal `visible` prop). */
  visible: boolean;
  /** Exercise name fed into the body copy interpolation. The caller MUST
   *  resolve LocalizedText → string (via `useExerciseName`) before
   *  passing it in — interpolating a LocalizedText object directly
   *  renders as `[object Object]` in the body copy. */
  name: string | null;
  /** Current slider value in seconds. */
  value: number;
  /** "Use as global default" checkbox state. */
  saveAsGlobal: boolean;
  /** Disables every control while a PATCH/PUT is in flight. */
  saving: boolean;
  /** Whether a per-exercise save target exists (restPickerSlug != null).
   *  When false and saveAsGlobal is off, the save button is locked. */
  canSavePerExercise: boolean;
  theme: ReturnType<typeof useTheme>;
  onValueChange: (seconds: number) => void;
  onToggleSaveAsGlobal: () => void;
  onCancel: () => void;
  onConfirm: () => void;
};

export function RestDefaultPicker({
  visible,
  name,
  value,
  saveAsGlobal,
  saving,
  canSavePerExercise,
  theme,
  onValueChange,
  onToggleSaveAsGlobal,
  onCancel,
  onConfirm,
}: RestDefaultPickerProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  if (!visible) return null;
  return (
    <Modal
      transparent
      animationType="fade"
      visible
      onRequestClose={onCancel}
    >
      <View style={styles.restPickerBackdrop}>
        <View style={styles.restPickerCard}>
          <Text style={styles.restPickerTitle}>
            {t('mobile.logger.restEditTitle')}
          </Text>
          <Text style={styles.restPickerBody}>
            {t('mobile.logger.restEditBody', {
              name: name ?? '',
            })}
          </Text>
          <RestStepper
            value={value}
            min={REST_PICKER_MIN}
            max={REST_PICKER_MAX}
            step={REST_PICKER_STEP}
            onChange={onValueChange}
            disabled={saving}
            presets={[30, 60, 90, 120, 180]}
            theme={theme}
          />
          {/* Inbox ddac68ab — tap toggles between "save to this
              exercise" (default) and "save as global default". The
              Pressable below owns the tap target + the a11y checkbox
              node (whole row, ≥44 via minHeight); the box below renders
              display-only, same split <ToggleRow> uses for its
              native <Switch>. */}
          <Pressable
            accessibilityRole="checkbox"
            accessibilityState={{ checked: saveAsGlobal }}
            {...stateAria({ checked: saveAsGlobal })}
            onPress={onToggleSaveAsGlobal}
            disabled={saving}
            style={({ pressed }) => [
              styles.restPickerGlobalRow,
              pressed && styles.restPickerActionPressed,
            ]}
          >
            <View
              style={[
                styles.restCheckbox,
                saveAsGlobal && styles.restCheckboxChecked,
              ]}
            >
              {saveAsGlobal ? (
                <Icon
                  name="check"
                  size={15} // 0.7 × the 22px box (restCheckbox); keep in step if the box is retuned
                  color={theme.palette.inkOnBrand}
                />
              ) : null}
            </View>
            <Text style={styles.restPickerGlobalLabel}>
              {t('mobile.logger.restEditSaveAsGlobal', {
                defaultValue: 'Use as global default',
              })}
            </Text>
          </Pressable>
          <View style={styles.restPickerActions}>
            <Pressable
              accessibilityRole="button"
              onPress={onCancel}
              disabled={saving}
              hitSlop={8}
              style={({ pressed }) => [
                styles.restPickerActionButton,
                styles.restPickerActionCancel,
                pressed && styles.restPickerActionPressed,
              ]}
            >
              <Text style={styles.restPickerActionTextCancel}>
                {t('mobile.logger.restEditCancel')}
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              onPress={onConfirm}
              disabled={saving || (!saveAsGlobal && !canSavePerExercise)}
              hitSlop={8}
              style={({ pressed }) => [
                styles.restPickerActionButton,
                styles.restPickerActionSave,
                (saving || (!saveAsGlobal && !canSavePerExercise)) &&
                  styles.restPickerActionLocked,
                pressed && styles.restPickerActionPressed,
              ]}
            >
              <Text style={styles.restPickerActionTextSave}>
                {saving
                  ? t('mobile.logger.restEditSaving')
                  : t('mobile.logger.restEditSave')}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}
